import { useMemo } from 'react';
import type { ModularComponent, RenderableModularComponent } from './ModularPageWrapper';
type ShopCategoryGridModule = Extract<ModularComponent, {
  type: 'shopCategoryGrid';
}>;
const getShopCategoryGridId = (index: number) => `shop-category-grid-${index + 1}`;
const isShopCategoryGridModule = (module: ModularComponent): module is ShopCategoryGridModule => module.type === 'shopCategoryGrid';

/**
 * Builds a lookup from source type and ID to the index of the final grid module
 * that renders that source. Used to decide which grid is allowed to show the
 * "Show More" button.
 */
const findLastGridIndexBySource = (modules: ModularComponent[]): Map<string, number> => {
  const lastIndexBySource = new Map<string, number>();
  modules.forEach((module, moduleIndex) => {
    if (isShopCategoryGridModule(module)) {
      // each pass overwrites the previous entry, so the map ends up holding
      // the final occurrence of each source.
      lastIndexBySource.set(getShopProductSourceKey(module), moduleIndex);
    }
  });
  return lastIndexBySource;
};
const getShopProductSourceKey = (module: ShopCategoryGridModule) => `${module.props.sourceType}:${module.props.categoryId}`;
export const useShopCategoryGridProps = (modules: ModularComponent[]): RenderableModularComponent[] => useMemo(() => {
  // find the last grid per source so that the Show More button is only added to that one.
  const lastGridIndexBySource = findLastGridIndexBySource(modules);
  const previousGridIdBySource = new Map<string, string>();
  let shopCategoryGridIndex = 0;
  return modules.map((module, index) => {
    if (!isShopCategoryGridModule(module)) return module;
    const sourceKey = getShopProductSourceKey(module);
    const gridId = getShopCategoryGridId(shopCategoryGridIndex);
    shopCategoryGridIndex += 1;
    const previousGridId = previousGridIdBySource.get(sourceKey);

    // record the grid as the previous grid for any later modules that share a source.
    previousGridIdBySource.set(sourceKey, gridId);
    return {
      ...module,
      props: {
        ...module.props,
        gridId,
        previousGridId,
        // enable loading more only for the last grid of a source to prevent duplicate Show More buttons
        showMoreEnabled: lastGridIndexBySource.get(sourceKey) === index
      }
    };
  });
}, [modules]);